import { useState, useCallback, useEffect } from 'react';
import { Download, X, Filter, ArrowUpDown } from 'lucide-react';
import reportingApi from '../../../api/reporting';
import { toast } from 'react-hot-toast';
import ReportChart from './ReportChart';
import EditorToolbar from './EditorToolbar';
import SlideSelector from './SlideSelector';
import SlideReorderer from './SlideReorderer';

const DEFAULT_METRIC_KEYS = ['investment', 'clicks', 'conversions', 'roas'];
const DEFAULT_BUDGET_METRIC_KEYS = ['investment', 'conversions'];
const BUDGET_PLATFORMS = [
  { key: 'google', label: 'Google' },
  { key: 'meta', label: 'Meta' }
];

const SlideEditorCarousel = ({ slidesData, filename, pptxBase64, config, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [editedSlides, setEditedSlides] = useState(slidesData);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [isSlidesSelectorOpen, setIsSlidesSelectorOpen] = useState(false);
  const [isSlideReordererOpen, setIsSlideReordererOpen] = useState(false);
  const [metricSlots, setMetricSlots] = useState(DEFAULT_METRIC_KEYS);
  const [budgetMetricSelections, setBudgetMetricSelections] = useState({});

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      reportingApi.downloadPPT(pptxBase64, filename);
      toast.success('Reporte descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PPTX:', error);
      toast.error('Error al descargar el reporte');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleContentChange = (slideIndex, elementId, newContent) => {
    const updatedSlides = [...editedSlides];
    const slide = updatedSlides[slideIndex];

    // Actualizar elemento editable
    const elementIndex = slide.editable_elements.findIndex(e => e.id === elementId);
    if (elementIndex !== -1) {
      updatedSlides[slideIndex].editable_elements[elementIndex].content = newContent;
      setEditedSlides(updatedSlides);
    }
  };

  const handleTableCellChange = (slideIndex, rowId, cellId, newContent) => {
    const updatedSlides = [...editedSlides];
    const slide = updatedSlides[slideIndex];

    if (slide.table && slide.table.rows) {
      const row = slide.table.rows.find(r => r.id === rowId);
      if (row) {
        const cell = row.cells.find(c => c.id === cellId);
        if (cell && cell.editable) {
          cell.content = newContent;
          setEditedSlides(updatedSlides);
        }
      }
    }
  };

  const handleBudgetMetricChange = (slideId, slotIndex, metricKey, slide) => {
    setBudgetMetricSelections((prev) => {
      const current = prev[slideId] || [...(slide.chart?.default_metric_keys || DEFAULT_BUDGET_METRIC_KEYS)];
      const updated = [...current];
      updated[slotIndex] = metricKey;
      return {
        ...prev,
        [slideId]: updated
      };
    });
  };

  const formatBudgetMetricValue = (value, format, currency) => {
    const safeValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (format === 'currency') {
      const formatted = safeValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return currency ? `${currency} ${formatted}` : formatted;
    }
    if (format === 'ratio') {
      return `${safeValue.toFixed(2)}x`;
    }
    if (format === 'percent') {
      return `${safeValue.toFixed(2)}%`;
    }
    return safeValue.toFixed(2);
  };

  const renderVariationDetails = (variation) => {
    if (!variation) return null;

    const rows = [
      { label: 'Periodo anterior', value: variation.previous_display },
      { label: 'Variación', value: variation.delta_display },
      { label: 'Diferencia absoluta', value: variation.difference_display },
      { label: '% cambio vs anterior', value: variation.percent_display }
    ];

    return (
      <div className="text-[11px] text-gray-500 space-y-0.5 mt-2">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between">
            <span className="font-medium">{row.label}</span>
            <span className="font-semibold text-gray-900">
              {row.value ?? '—'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleFormat = (type, value) => {
    if (!selectedElement) return;

    const updatedSlides = [...editedSlides];
    const slide = updatedSlides[currentSlide];
    const element = slide.editable_elements?.find(el => el.id === selectedElement.id);

    if (element) {
      if (!element.style) element.style = {};

      if (type === 'bold') {
        element.style.fontWeight =
          element.style.fontWeight === 'bold' ? 'normal' : 'bold';
      } else if (type === 'italic') {
        element.style.fontStyle =
          element.style.fontStyle === 'italic' ? 'normal' : 'italic';
      } else if (type === 'reset') {
        element.style = {};
      } else if (type === 'fontSize' || type === 'color') {
        element.style[type] = value;
      }

      setEditedSlides(updatedSlides);

      // Actualizar elemento seleccionado para reflejar cambios
      setSelectedElement({ ...element });
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft' && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (e.key === 'ArrowRight' && currentSlide < editedSlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [currentSlide, editedSlides.length, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const current = editedSlides[currentSlide];
    if (current?.type === 'metrics') {
      setMetricSlots(DEFAULT_METRIC_KEYS);
    }
  }, [currentSlide, editedSlides]);

  useEffect(() => {
    setBudgetMetricSelections((prev) => {
      let next = { ...prev };
      let mutated = false;
      editedSlides.forEach((slide) => {
        if (slide.type !== 'budget_distribution') return;
        if (next[slide.id]) return;
        const chunk = slide.chart?.default_metric_keys;
        next[slide.id] = Array.isArray(chunk) ? [...chunk] : [...DEFAULT_BUDGET_METRIC_KEYS];
        mutated = true;
      });
      return mutated ? next : prev;
    });
  }, [editedSlides]);

  const handleMetricChange = (slotIndex, newMetric) => {
    setMetricSlots((prev) =>
      prev.map((value, idx) => (idx === slotIndex ? newMetric : value))
    );
  };

  const renderMetricsSlide = (slide) => {
    const availableMetrics = slide.metrics || [];
    if (!availableMetrics.length) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">No hay métricas disponibles</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        {metricSlots.map((metricKey, idx) => {
          const metric =
            availableMetrics.find((m) => m.key === metricKey) ||
            availableMetrics[idx] ||
            availableMetrics[0];
          if (!metric) return null;

          return (
            <div
              key={`${slide.id}-metric-${idx}`}
              className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-sm font-semibold text-gray-700">{metric.title}</span>
              </div>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1 text-sm font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={metricKey}
                  onChange={(e) => handleMetricChange(idx, e.target.value)}
                  aria-label={`Seleccionar métrica para tarjeta ${idx + 1}`}
                >
                  {availableMetrics.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.title}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-3xl font-bold text-gray-900">{metric.display_value}</span>
              <span className="text-sm text-gray-600">{metric.comparison_text}</span>
              {renderVariationDetails(metric.variation)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBudgetDistributionSlide = (slide) => {
    const chart = slide.chart || {};
    const pieData = Array.isArray(chart.data) ? chart.data : [];
    if (!pieData.length) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
          No hay datos de distribución disponibles
        </div>
      );
    }
    const metricOptions = chart.metric_options?.length
      ? chart.metric_options
      : DEFAULT_BUDGET_METRIC_KEYS.map((metricKey) => ({
          key: metricKey,
          title: metricKey.charAt(0).toUpperCase() + metricKey.slice(1),
          format: 'currency'
        }));
    const platformMetrics = chart.platform_metrics || {};
    const rawSelections = budgetMetricSelections[slide.id];
    const selections = Array.isArray(rawSelections) ? rawSelections : [...(slide.chart?.default_metric_keys || DEFAULT_BUDGET_METRIC_KEYS)];
    const currencyLabel = chart.currency;

    return (
      <div className="w-full h-full flex flex-col gap-6">
        <div className="w-full flex flex-col gap-6 md:flex-row">
          <div className="w-full md:w-1/2 flex justify-center">
            <div className="w-full max-w-[360px] rounded-lg border border-dashed border-blue-200 bg-white/90 p-6 shadow-sm">
              <ReportChart
                type={chart.type || 'pie'}
                data={pieData}
                config={config}
                height={chart.height || 220}
                valueFormatter={(value) => Number(value || 0).toFixed(2)}
              />
              {currencyLabel && (
                <p className="text-xs text-gray-500 mt-2 text-right">Distribución en {currencyLabel}</p>
              )}
            </div>
          </div>

          <div className="w-full md:w-1/2 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              {selections.slice(0, 2).map((metricKey, idx) => {
                return (
                  <label key={`metric-selector-${idx}`} className="flex flex-col gap-2 text-sm">
                    <span className="font-semibold text-gray-700">Métrica {idx + 1}</span>
                    <select
                      value={metricKey}
                      className="border border-gray-300 rounded-md px-3 py-1 bg-white text-sm"
                      onChange={(event) =>
                        handleBudgetMetricChange(slide.id, idx, event.target.value, slide)
                      }
                    >
                      {metricOptions.map((optionItem) => (
                        <option key={`metric-option-${idx}-${optionItem.key}`} value={optionItem.key}>
                          {optionItem.title}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {BUDGET_PLATFORMS.map((platform) =>
                selections.slice(0, 2).map((metricKey, slotIndex) => {
                  const option = metricOptions.find((opt) => opt.key === metricKey);
                  const metricLabel =
                    option?.title || chart.metric_labels?.[metricKey] || 'Métrica seleccionada';
                  const format = option?.format || 'count';
                  const rawValue = platformMetrics[platform.key]?.[metricKey] ?? 0;
                  const formattedValue = formatBudgetMetricValue(rawValue, format, currencyLabel);

                  return (
                    <div
                      key={`${platform.key}-budget-${slotIndex}`}
                      className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col gap-3"
                    >
                      <p className="text-xs text-gray-500 uppercase">
                        {platform.label} #{slotIndex + 1}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">{metricLabel}</p>
                      <p className="text-3xl font-semibold text-blue-600">{formattedValue}</p>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSlide = (slide, slideIndex) => {
    if (slide.type === 'metrics') {
      return renderMetricsSlide(slide);
    }
    if (slide.type === 'budget_distribution') {
      return renderBudgetDistributionSlide(slide);
    }
    const bgColor = slide.background?.color || '#FFFFFF';

    return (
      <div
        className="w-full h-full p-6 sm:p-10 relative overflow-auto"
        style={{ backgroundColor: bgColor }}
      >
        {/* Renderizar elementos editables */}
        {slide.editable_elements?.map((element) => (
          <div key={element.id} className="mb-4">
            {element.type === 'metric' ? (
              <div className="mb-6">
                <div className="text-sm font-semibold text-gray-700 mb-1">{element.label}</div>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => handleContentChange(slideIndex, element.id, e.target.textContent)}
                  onClick={() => {
                    setSelectedElement(element);
                    setShowToolbar(true);
                  }}
                  className="outline-none focus:ring-2 focus:ring-blue-500 p-2 rounded cursor-pointer hover:bg-blue-50/30 transition-colors"
                  style={{
                    fontSize: element.style?.fontSize || '28px',
                    fontWeight: element.style?.fontWeight || 'bold',
                    color: element.style?.color || '#000',
                    cursor: 'text'
                  }}
                  title="Click para editar estilos"
                >
                  {element.value || element.content}
                </div>
              </div>
            ) : element.type === 'optimization' ? (
              <div className="mb-6 border-l-4 border-blue-500 pl-4 bg-gray-50 p-3 rounded">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 mb-1">{element.title}</div>
                    <div className="text-sm text-gray-700 mb-2">{element.description}</div>
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span className="bg-blue-100 px-2 py-1 rounded">{element.status}</span>
                      <span className="bg-green-100 px-2 py-1 rounded">{element.type_badge}</span>
                      <span className="bg-purple-100 px-2 py-1 rounded">{element.platform}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleContentChange(slideIndex, element.id, e.target.textContent)}
                onClick={() => {
                  setSelectedElement(element);
                  setShowToolbar(true);
                }}
                className="outline-none focus:ring-2 focus:ring-blue-500 p-2 rounded cursor-pointer hover:bg-blue-50/30 transition-colors"
                style={{
                  fontSize: element.style?.fontSize || '16px',
                  fontWeight: element.style?.fontWeight || 'normal',
                  color: element.style?.color || '#000',
                  textAlign: element.style?.textAlign || 'left',
                  whiteSpace: element.style?.whiteSpace || 'normal',
                  cursor: 'text'
                }}
                title="Click para editar estilos"
              >
                {element.content}
              </div>
            )}
          </div>
        ))}

        {/* Renderizar gráficos si existen */}
        {slide.chart && (
          <div className="mt-8 mb-6">
            <ReportChart
              type={slide.chart.type}
              data={slide.chart.data}
              config={config}
              height={slide.chart.height || 300}
            />
          </div>
        )}

        {/* Renderizar tabla si existe */}
        {slide.table && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {slide.table.headers?.map((header, idx) => (
                    <th
                      key={idx}
                      className="border border-gray-300 px-3 py-2 text-left font-bold text-sm"
                      style={{ backgroundColor: config?.primaryColor || '#0F31F7', color: '#FFFFFF' }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slide.table.rows?.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.cells?.map((cell) => (
                      <td
                        key={cell.id}
                        className="border border-gray-300 px-3 py-2 text-sm"
                      >
                        {cell.editable ? (
                          <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => handleTableCellChange(slideIndex, row.id, cell.id, e.target.textContent)}
                            className="outline-none focus:ring-1 focus:ring-blue-500 cursor-text"
                          >
                            {cell.content}
                          </div>
                        ) : (
                          <div>{cell.content}</div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 modal-safe-area bg-black bg-opacity-75 flex items-center justify-center z-50 px-3 py-6 sm:px-6 sm:py-8 overflow-y-auto">
      {/* Toolbar - Mostrar cuando hay elemento seleccionado */}
      {showToolbar && selectedElement && (
        <EditorToolbar
          onFormat={handleFormat}
          onClose={() => {
            setShowToolbar(false);
            setSelectedElement(null);
          }}
          selectedElement={selectedElement}
        />
      )}

      {/* Selector de Slides */}
      <SlideSelector
        slides={editedSlides}
        onSlidesChange={(filteredSlides) => {
          setEditedSlides(filteredSlides);
          setCurrentSlide(0);
          toast.success(`${filteredSlides.length} slides seleccionados`);
        }}
        isOpen={isSlidesSelectorOpen}
        onClose={() => setIsSlidesSelectorOpen(false)}
      />

      {/* Reordenador de Slides */}
      <SlideReorderer
        slides={editedSlides}
        onReorder={(reorderedSlides) => {
          setEditedSlides(reorderedSlides);
          setCurrentSlide(0);
          toast.success('Orden de slides actualizado');
        }}
        isOpen={isSlideReordererOpen}
        onClose={() => setIsSlideReordererOpen(false)}
      />

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[min(960px,90vw)] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 p-4 border-b bg-gradient-to-r from-blue-600 to-cyan-600">
          <div>
            <h2 className="text-2xl font-bold text-white">Editor de Presentación</h2>
            <p className="text-sm text-white/80">Edita los textos directamente haciendo clic sobre ellos</p>
            {showToolbar && selectedElement && (
              <p className="text-xs text-blue-100 mt-1">💡 Use el toolbar superior para cambiar estilos</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {/* Slide Display */}
          <div
            className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-inner border-2 border-gray-200 h-[min(65vh,520px)] sm:h-[min(75vh,560px)]"
          >
            {editedSlides && editedSlides.length > 0 ? (
              <>
                {renderSlide(editedSlides[currentSlide], currentSlide)}

              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No hay slides disponibles</p>
              </div>
            )}
          </div>

          {/* Slide counter + thumbnails responsive */}
          {editedSlides && editedSlides.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="text-center text-gray-600 text-sm sm:text-base">
                Slide {currentSlide + 1} de {editedSlides.length}
              </div>

              {editedSlides.length > 1 && (
                <div className="flex flex-wrap gap-2 overflow-x-auto scrollbar-hide pb-2">
                  {editedSlides.map((slide, idx) => (
                    <button
                      key={slide.id}
                      onClick={() => setCurrentSlide(idx)}
                      className={`flex-shrink-0 border-2 rounded-lg overflow-hidden transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        idx === currentSlide
                          ? 'border-blue-500 shadow-lg scale-110'
                          : 'border-gray-300 hover:border-gray-400 opacity-70 hover:opacity-100'
                      }`}
                      style={{
                        width: 'clamp(90px, 26vw, 140px)',
                        height: 'clamp(70px, 20vw, 110px)',
                        position: 'relative'
                      }}
                      title={`Slide ${idx + 1}`}
                    >
                      {/* Preview visual simplificado del slide */}
                      <div
                        className="w-full h-full p-2 text-[10px] overflow-hidden flex flex-col justify-between"
                        style={{
                          backgroundColor: slide.background?.color || '#FFFFFF',
                          color: slide.background?.textColor || '#000'
                        }}
                      >
                        {/* Mostrar primeros elementos de contenido */}
                        <div className="space-y-1">
                          {slide.editable_elements?.slice(0, 2).map((el, i) => (
                            <div
                              key={i}
                              className="text-gray-700 truncate font-semibold"
                            >
                              {el.content?.substring(0, 20) ||
                                el.value?.toString().substring(0, 20) ||
                                el.title?.substring(0, 20) ||
                                '—'}
                            </div>
                          ))}
                        </div>

                        {/* Mostrar tipo de slide en esquina */}
                        <div className="text-[11px] font-semibold text-gray-600">
                          Slide {idx + 1}
                        </div>
                      </div>

                      {/* Indicador de contenido */}
                      {slide.table && (
                        <div
                          className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1"
                          title="Contiene tabla"
                        >
                          <span className="text-xs font-bold">📊</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => setIsSlidesSelectorOpen(true)}
              className="flex items-center justify-center p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow transition-colors"
              title="Seleccionar qué slides incluir/excluir"
              aria-label="Seleccionar slides"
            >
              <Filter size={20} />
              <span className="sr-only">Seleccionar slides</span>
            </button>

            <button
              onClick={() => setIsSlideReordererOpen(true)}
              className="flex items-center justify-center p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow transition-colors"
              title="Reordenar slides con drag & drop"
              aria-label="Reordenar slides"
            >
              <ArrowUpDown size={20} />
              <span className="sr-only">Reordenar slides</span>
            </button>

            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={20} />
              {isDownloading ? 'Descargando...' : 'Descargar presentación'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideEditorCarousel;
